/**
 * The stored record of the shell, under `vms.panels.v3`. The seed of a store fills it, and each
 * write of it also writes storage. Thus, the next store, of the next sign-in or organization,
 * reads what this one wrote.
 * The writes here change only the record; `shellPanelsAtoms.ts` has the writes that navigate.
 */

import { atom } from 'jotai';

import { mergePanelWidths } from '@/app/panelResize';
import { isForeignOrgEntry, isSameIdsArray, parsePanelIds } from '@/app/panelUrl';
import { clampChatWidth } from '@/app/shell-v3/chatWidth';
import { clampNavWidth, showsLabels } from '@/app/shell-v3/navResize';
import { authSession } from '@/auth/authSession';
import type {
    PanelId, PanelLocalProps, PersistedShared, ShellRecord, UserWidths,
} from '@/types/panels';

import {
    DEFAULT_SHELL_RECORD, readPersistedShared, readShellRecord, writeShellRecord,
} from './shellRecordStorage';
import { findSavedFocus } from './viewFocusStorage';

/** The part of a shell location that the record copies. */
interface ShellEntry {
    search: string;
    state: unknown;
}

const recordStateAtom = atom<ShellRecord>(DEFAULT_SHELL_RECORD);

// Read-only: the write atoms below are the only writers.
export const shellRecordAtom = atom((get): ShellRecord => get(recordStateAtom));

export const navWidthAtom = atom((get): number => get(recordStateAtom).navWidth);
export const chatWidthAtom = atom((get): number => get(recordStateAtom).chatWidth);
export const focusedIdAtom = atom((get): PanelId | null => get(recordStateAtom).focusedId);
export const userWidthsAtom = atom((get): UserWidths => get(recordStateAtom).userWidths);
export const storedOpenPanelIdsAtom = atom((get): PanelId[] => get(recordStateAtom).openPanels);
export const localPropsAtom = atom((get): PanelLocalProps => get(recordStateAtom).scratch);
export const persistedSharedAtom = atom((get): PersistedShared => get(recordStateAtom).shared);
export const isNavLabelShownAtom = atom(
    (get): boolean => showsLabels(get(recordStateAtom).navWidth));

export const seedShellRecordAtom = atom(null, (_get, set): void => {
    set(recordStateAtom, readShellRecord() ?? DEFAULT_SHELL_RECORD);
});

/** Takes an updater, so two writes of one tick compose. Storage gets each change once. */
export const updateShellRecordAtom = atom(
    null, (get, set, update: (record: ShellRecord) => ShellRecord): void => {
        const record = get(recordStateAtom);
        const nextShellRecord = update(record);
        if (nextShellRecord === record) {
            return;
        }
        writeShellRecord(nextShellRecord);
        set(recordStateAtom, nextShellRecord);
    });

// The gates of the shell route show the shell only to a page signed in to an organization, so
// another page keeps the stored workspace.
function isShellShown(): boolean {
    const { isSignedIn, orgId } = authSession.getSnapshot();
    return isSignedIn && orgId !== null;
}

export const setNavWidthAtom = atom(null, (_get, set, nextPx: number): void => {
    const width = clampNavWidth(nextPx);
    set(updateShellRecordAtom, (record): ShellRecord => (
        record.navWidth === width ? record : { ...record, navWidth: width }
    ));
});

export const setChatWidthAtom = atom(null, (_get, set, nextPx: number): void => {
    const width = clampChatWidth(nextPx);
    set(updateShellRecordAtom, (record): ShellRecord => (
        record.chatWidth === width ? record : { ...record, chatWidth: width }
    ));
});

// The widths of a resize drag in one shallow merge, so that the writes of a cascade are atomic.
export const setPanelWidthsAtom = atom(null, (_get, set, updates: UserWidths): void => {
    if (Object.keys(updates).length === 0) {
        return;
    }
    set(updateShellRecordAtom, (record): ShellRecord => {
        const userWidths = mergePanelWidths(record.userWidths, updates);
        return userWidths === record.userWidths ? record : { ...record, userWidths };
    });
});

// A panel without a set width reverts to its declared default.
export const resetPanelWidthAtom = atom(null, (_get, set, id: string): void => {
    set(updateShellRecordAtom, (record): ShellRecord => {
        if (!Object.hasOwn(record.userWidths, id)) {
            return record;
        }
        const nextValue = { ...record.userWidths };
        delete nextValue[id];
        return { ...record, userWidths: nextValue };
    });
});

/**
 * Keeps the record's two copies of a shell URL, the open ids and the remembered camera, for the
 * restore of a bare URL. A URL that names `panels=` is authoritative. A key that the URL does not
 * name keeps its remembered value. An entry written under another organization gives its ids but
 * not its camera. The gates of the shell route show the shell only to a page signed in to an
 * organization, so another page keeps the stored workspace.
 */
export const copyUrlIntoRecordAtom = atom(null, (_get, set, entry: ShellEntry): void => {
    const params = new URLSearchParams(entry.search);
    const panels = params.get('panels');
    if (panels === null || !isShellShown()) {
        return;
    }
    const ids = parsePanelIds(panels);
    const urlShared = isForeignOrgEntry(entry.state) ? {} : readPersistedShared(params);
    set(updateShellRecordAtom, (record): ShellRecord => {
        let shared: PersistedShared | null = null;
        for (const [key, value] of Object.entries(urlShared)) {
            if (value !== record.shared[key]) {
                shared = shared ?? { ...record.shared };
                shared[key] = value;
            }
        }
        const isSameIds = isSameIdsArray(ids, record.openPanels);
        if (isSameIds && !shared) {
            return record;
        }
        return {
            ...record,
            openPanels: isSameIds ? record.openPanels : ids,
            shared: shared ?? record.shared,
        };
    });
});

/** Puts the panel that was in front of the view that `search` shows back in front. */
export const restoreViewFocusAtom = atom(null, (_get, set, search: string): void => {
    if (!isShellShown()) {
        return;
    }
    const savedFocus = findSavedFocus(search);
    if (!savedFocus) {
        return;
    }
    const [, savedPanelId] = savedFocus;
    set(updateShellRecordAtom, (record): ShellRecord => (
        record.focusedId === savedPanelId ? record : { ...record, focusedId: savedPanelId }
    ));
});
